<template>
    <div
        class="sbs-file-list"
        :class="fileListClasses">
        <SbsDraggable
            v-if="isFileListVisible"
            class="sbs-file-list__preview"
            v-bind="fileListWrapperData"
            v-on="fileListWrapperListeners">
            <div
                v-for="(file, i) in files"
                :key="file.id || i"
                class="sbs-file-list__item">
                <!-- @slot Слот для состояния просмотра файла -->
                <slot
                    name="preview"
                    :file="file"
                    :index="i">
                    <SbsFilePreviewer
                        v-bind="getPreviewerData(file)"
                        @removeFile="removeFile(i)" />
                </slot>
            </div>
        </SbsDraggable>

        <div
            v-if="isUploaderVisible"
            class="sbs-file-list__upload">
            <SbsFileUploader
                class="sbs-file-list__uploader"
                v-bind="uploaderData"
                @dragenter="uploaderDragenterHandler"
                @dragleave="uploaderDragleaveHandler"
                @drop="uploaderDropHandler"
                @focus="uploaderFocusHandler"
                @blur="uploaderBlurHandler"
                @click="uploaderClickHandler"
                @change="uploaderChangeHandler">
                <!-- @slot Слот для состояния ожидания -->
                <slot name="upload"></slot>
            </SbsFileUploader>
        </div>
    </div>
</template>

<script>
import { AllColors } from '../../utils/colors.js';
import { getFileExtension } from '../../utils/fileTypes.js';
import SbsDraggable from '../draggable/SbsDraggable.vue';

const KB_SIZE = 1024;

export default {
    name: 'SbsFileList',
    emits: [
        'update:modelValue',
        'dragenter',
        'dragleave',
        'focus',
        'blur',
        'click',
        'drop',
        'add',
        'removeFile',
        'error:maxFilesLimit',
        'error:fileExtension',
        'error:maxFileSize',
    ],
    components: {
        SbsDraggable,
    },
    computed: {
        fileListClasses() {
            return [
                {
                    disabled: this.disabled,
                },
            ];
        },
        isFileListVisible() {
            return Boolean(this.files.length && this.showFileList);
        },
        files() {
            return [...this.modelValue];
        },
        fileListWrapperData() {
            return this.draggable
                ? this.draggableOptions
                : { disabled: true };
        },
        fileListWrapperListeners() {
            return this.draggable
                ? this.draggableListeners
                : {};
        },
        availableFilesLimit() {
            const maxFilesLimit = this.multiple ? this.maxFilesLimit : 1;

            return Math.max(maxFilesLimit - this.files.length, 0);
        },
        previewerData() {
            return {
                cancelIconTheme: this.cancelIconTheme,
                class: this.filePreviewerClass,
                downloadable: this.downloadable,
                downloadIconTheme: this.downloadIconTheme,
                fileInfoTheme: this.fileInfoTheme,
                fileNameTheme: this.fileNameTheme,
                loaderBorderRadius: this.loaderBorderRadius,
                loaderStrokeWidth: this.loaderStrokeWidth,
            };
        },
        isUploaderVisible() {
            return !this.files.length || this.showUploader;
        },
        uploaderData() {
            return {
                ...this.$attrs,
                acceptFileExtensions: this.acceptFileExtensions,
                backgroundTheme: this.fileUploaderBackgroundTheme,
                bordered: this.fileUploaderBordered,
                borderTheme: this.fileUploaderBorderTheme,
                colorTheme: this.fileUploaderColorTheme,
                class: this.fileUploaderClass,
                disabled: this.fileUploaderDisabled,
                dragAndDrop: this.fileUploaderDragAndDrop,
                errorMessages: this.errorMessages,
                files: this.modelValue,
                hoverBackground: this.fileUploaderHoverBackground,
                hoverBorder: this.fileUploaderHoverBorder,
                hoverColor: this.fileUploaderHoverColor,
                labelClass: this.fileUploaderLabelClass,
                maxFileSize: this.maxFileSize,
                multiple: this.multiple,
            };
        },
    },
    methods: {
        getPreviewerData(file = {}) {
            return {
                ...this.previewerData,
                file,
                disabled: file.disabled,
                fileBackgroundTheme: file.fileBackgroundTheme || this.fileBackgroundTheme,
                fileHoverBackground: file.fileHoverBackground || this.fileHoverBackground,
                fileHref: file.href,
                fileIcon: file.fileIcon || this.fileIcon,
                fileIconTheme: file.fileIconTheme || this.fileIconTheme,
                loaderStrokeTheme: file.loaderStrokeTheme || this.loaderStrokeTheme,
                progress: file.progress,
            };
        },
        removeFile(index) {
            const files = [...this.files];
            const [removedFile] = files.splice(index, 1);

            this.$emit('update:modelValue', files);
            this.$emit('removeFile', removedFile);
        },
        uploaderDragenterHandler(e) {
            this.$emit('dragenter', e);
        },
        uploaderDragleaveHandler(e) {
            this.$emit('dragleave', e);
        },
        uploaderDropHandler(e) {
            this.$emit('drop', e);
        },
        uploaderFocusHandler(e) {
            this.$emit('focus', e);
        },
        uploaderBlurHandler(e) {
            this.$emit('blur', e);
        },
        uploaderClickHandler(e) {
            this.$emit('click', e);
        },
        async uploaderChangeHandler(files) {
            if (typeof this.beforeChange === 'function') {
                await this.beforeChange();
            }

            const uploadingFiles = [...files].filter(this.validateFile);

            if (!uploadingFiles.length) return;

            if (uploadingFiles.length > this.availableFilesLimit) {
                const extraFiles = uploadingFiles
                    .splice(this.availableFilesLimit);

                this.$emit('error:maxFilesLimit', extraFiles);
            }

            this.$emit('add', uploadingFiles);
            this.$emit('update:modelValue', [...this.files, ...uploadingFiles]);
        },
        validateFile(file) {
            let isValid = true;
            const fileName = (file?.name ?? '').toLowerCase();
            const ext = `.${getFileExtension(fileName)}`;

            if (
                this.acceptFileExtensions.length &&
                this.acceptFileExtensions.indexOf(ext) < 0
            ) {
                isValid = false;
                this.$emit('error:fileExtension', file);
            }

            if (file.size > this.maxFileSize * KB_SIZE * KB_SIZE) {
                isValid = false;
                this.$emit('error:maxFileSize', file);
            }

            return isValid;
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Допустимые типы файлов
         */
        acceptFileExtensions: {
            type: Array,
            default: () => [],
        },
        /**
         * Коллбэк, срабатываемый после выбора файлов в проводнике, но перед событием change
         */
        beforeChange: {
            type: Function,
            default: null,
        },
        /**
         * Цвет иконки отмены загрузки/скачивания
         */
        cancelIconTheme: {
            type: String,
            default: AllColors.GRAY,
        },
        /**
         * Отключает любые действия с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность скачивания
         */
        downloadable: {
            type: Boolean,
            default: false,
        },
        /**
         * Цвет иконки загрузки/скачивания
         */
        downloadIconTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Включает возможность менять файлы местами с помощью драг-н-дропа.
         */
        draggable: {
            type: Boolean,
            default: false,
        },
        /**
         * Объект со слушателями событий `SbsDraggable`
         */
        draggableListeners: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Объект с настройками `SbsDraggable`
         */
        draggableOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Сообщения об ошибках
         */
        errorMessages: {
            type: Array,
            default: () => [],
        },
        /**
         * Цвет фона под иконкой файла
         */
        fileBackgroundTheme: {
            type: String,
            default: AllColors.GRADIENT_PALE_BLUE,
        },
        /**
         * Цвет фона под иконкой при наведении
         */
        fileHoverBackground: {
            type: String,
            default: AllColors.GRAY_ULTRA_LIGHT,
        },
        /**
         * Иконка файла
         */
        fileIcon: {
            type: String,
            default: 'file',
        },
        /**
         * Цвет иконки файла
         */
        fileIconTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Цвет информации о файле
         */
        fileInfoTheme: {
            type: String,
            default: AllColors.GRAY_DEEP,
        },
        /**
         * Цвет имени файла
         */
        fileNameTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Класс для блока с загруженным файлом
         */
        filePreviewerClass: {
            type: String,
            default: '',
        },
        /**
         * Цвет фона для поля загрузки файла
         */
        fileUploaderBackgroundTheme: {
            type: String,
            default: '',
        },
        /**
         * Включает бордер для поля загрузки файла
         */
        fileUploaderBordered: {
            type: Boolean,
            default: false,
        },
        /**
         * Цвет бордера для поля загрузки файла
         */
        fileUploaderBorderTheme: {
            type: String,
            default: '',
        },
        /**
         * Класс для поля загрузки файла
         */
        fileUploaderClass: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета текста для поля загрузки файла
         */
        fileUploaderColorTheme: {
            type: String,
            default: '',
        },
        /**
         * Отключает поле загрузки файла
         */
        fileUploaderDisabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Включает возможность добавлять файлы перетаскиванием в поле загрузки файла
         */
        fileUploaderDragAndDrop: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета фона для поля загрузки файла при наведении
         */
        fileUploaderHoverBackground: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета бордера для поля загрузки файла при наведении
         */
        fileUploaderHoverBorder: {
            type: String,
            default: '',
        },
        /**
         * Тема (цветовые решения) для цвета текста для поля загрузки файла при наведении
         */
        fileUploaderHoverColor: {
            type: String,
            default: '',
        },
        /**
         * Класс для лейбла для поля загрузки файла
         */
        fileUploaderLabelClass: {
            type: String,
            default: '',
        },
        /**
         * Радиус полосы загрузки
         */
        loaderBorderRadius: {
            type: Number,
            default: 10,
        },
        /**
         * Цвет полосы загрузки
         */
        loaderStrokeTheme: {
            type: String,
            default: AllColors.PRIMARY,
        },
        /**
         * Толщина полосы загрузки
         */
        loaderStrokeWidth: {
            type: Number,
            default: 2,
        },
        /**
         * Допустимое количество одновременно добавленных файлов
         */
        maxFilesLimit: {
            type: Number,
            default: 10,
        },
        /**
         * Допустимый размер загружаемого файла в мегабайтах
         */
        maxFileSize: {
            type: Number,
            default: 70,
        },
        /**
         * Флаг загрузки нескольких файлов
         */
        multiple: {
            type: Boolean,
            default: false,
        },
        /**
         * Определяет видимость списка файлов
         */
        showFileList: {
            type: Boolean,
            default: true,
        },
        /**
         * Определяет видимость поля загрузки файла
         */
        showUploader: {
            type: Boolean,
            default: false,
        },
        /**
         * @model
         */
        modelValue: {
            type: Array,
            default: () => [],
        },
    },
};
</script>

<style lang="scss">
.sbs-file-list {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
        user-select: none;

        & > * {
            pointer-events: none;
        }
    }

    &__preview {
        display: flex;
        flex-direction: column;
        margin-bottom: 15px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__item {
        margin-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    &__upload {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__uploader {
        flex-grow: 1;
    }
}
</style>
